import { ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { colors, s8, shadowM } from '@ardoq/design-tokens';
import {
  anchorNameForId,
  HTMLElementWithAnchor,
  PopoverSide,
} from './Popover2025';
import {
  POPOVER_ARROW_SIZE as ARROW_SIZE,
  POPOVER_WINDOW_EDGE_MARGIN as WINDOW_EDGE_MARGIN,
  POPOVER_WRAPPER_PADDING,
} from './consts';

const POPOVER_GAP = 4;

export const PopoverPositioningContainer = styled.div<{
  $side: PopoverSide;
  $gap: number;
}>`
  position: relative;

  ${({ $side, $gap }) => {
    const totalMargin = ARROW_SIZE / 2 + $gap;
    switch ($side) {
      case 'top':
        return css`
          margin-bottom: ${totalMargin}px;
          margin-inline: ${WINDOW_EDGE_MARGIN}px;
        `;
      case 'bottom':
        return css`
          margin-top: ${totalMargin}px;
          margin-inline: ${WINDOW_EDGE_MARGIN}px;
        `;
      case 'left':
        return css`
          margin-right: ${totalMargin}px;
          margin-block: ${WINDOW_EDGE_MARGIN}px;
        `;
      case 'right':
        return css`
          margin-left: ${totalMargin}px;
          margin-block: ${WINDOW_EDGE_MARGIN}px;
        `;
    }
  }}
`;

const arrowSidePlacement = (side: PopoverSide, gap: number): string => {
  const offset = ARROW_SIZE / 2 + gap;
  switch (side) {
    case 'bottom':
      return `top: calc(anchor(bottom) + ${offset}px); left: anchor(center); transform: translate(-50%, -50%) rotate(45deg);`;
    case 'top':
      return `top: calc(anchor(top) - ${offset}px); left: anchor(center); transform: translate(-50%, -50%) rotate(-135deg);`;
    case 'right':
      return `left: calc(anchor(right) + ${offset}px); top: anchor(center); transform: translate(-50%, -50%) rotate(-45deg);`;
    case 'left':
      return `left: calc(anchor(left) - ${offset}px); top: anchor(center); transform: translate(-50%, -50%) rotate(135deg);`;
  }
};

const OPPOSITE_SIDE: Record<PopoverSide, PopoverSide> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
};

/**
 * Re-places the arrow via `@container anchored(fallback:)` when `position-try`
 * flips the popover: top/bottom flip on the block axis, left/right on the inline
 * axis.
 */
const arrowFlipPlacement = (side: PopoverSide, gap: number): string => {
  const flipped = arrowSidePlacement(OPPOSITE_SIDE[side], gap);
  const fallbacks =
    side === 'top' || side === 'bottom'
      ? ['flip-block', 'flip-block flip-inline']
      : ['flip-inline', 'flip-block flip-inline'];
  return fallbacks
    .map(
      fallback => `@container anchored(fallback: ${fallback}) { ${flipped} }`
    )
    .join('\n');
};

/**
 * Positions the arrow against the anchor via CSS `anchor()`: `anchor(center)` on
 * the cross axis and `anchor(<edge>) ± gap` on the other.
 *
 * `position: fixed` is required so the containing block is the viewport, which
 * contains the anchor — an `absolute` arrow's containing block would be the
 * popover, which is not an acceptable anchor element for `anchor()`. Corollary:
 * if any ancestor of the popover establishes a containing block (`transform`,
 * `will-change: transform`, `filter`, `perspective`, or `contain: layout/paint`)
 * the fixed arrow resolves against that ancestor instead and `anchor()` no
 * longer tracks the trigger.
 */
export const PopoverArrow = styled.div<{
  $side: PopoverSide;
  $anchorName: string;
  $gap: number;
}>`
  position: fixed;
  position-anchor: ${({ $anchorName }) => $anchorName};
  width: ${ARROW_SIZE}px;
  height: ${ARROW_SIZE}px;
  background: ${colors.surfaceDefault};
  box-shadow: -1px -1px 1px rgba(0, 0, 0, 0.1);

  ${({ $side, $gap }) => css`
    ${arrowSidePlacement($side, $gap)}
    ${arrowFlipPlacement($side, $gap)}
  `}
`;

const PopoverWrapper = styled.div<{ $maxHeight?: number }>`
  background: ${colors.surfaceDefault};
  box-shadow: ${shadowM};
  border-radius: ${s8};
  padding: ${POPOVER_WRAPPER_PADDING}px;
  overflow-wrap: break-word;

  ${({ $maxHeight }) =>
    $maxHeight !== undefined &&
    css`
      max-height: ${$maxHeight}px;
      overflow-y: auto;
    `}
`;

const VIEWPORT_SAFETY_MARGIN = 8;
const MIN_AVAILABLE_HEIGHT = 120;
const CONTAINER_MARGIN = ARROW_SIZE / 2 + POPOVER_GAP;

const getAvailableHeight = (anchor: Element, side: PopoverSide): number => {
  const rect = anchor.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  if (side === 'bottom') {
    return (
      viewportHeight - rect.bottom - CONTAINER_MARGIN - VIEWPORT_SAFETY_MARGIN
    );
  }
  if (side === 'top') {
    return rect.top - CONTAINER_MARGIN - VIEWPORT_SAFETY_MARGIN;
  }
  // For left/right placement the card extends vertically around the anchor, so
  // the limiting space is the viewport height itself.
  return viewportHeight - 2 * VIEWPORT_SAFETY_MARGIN;
};

/**
 * Caps the popover card to the space available on the side it actually
 * rendered on (after any `position-try` flip) and lets it scroll internally,
 * but only when the content would otherwise overflow the viewport. This is the
 * CSS-anchor equivalent of floating-ui's `size` middleware: when neither side
 * has enough room, the card fills the available space and scrolls rather than
 * spilling off-screen.
 */
const usePopoverMaxHeight = ({
  isOpen,
  actualSide,
  anchorRef,
  wrapperRef,
}: {
  isOpen: boolean;
  actualSide: PopoverSide;
  anchorRef: RefObject<HTMLElementWithAnchor | null>;
  wrapperRef: RefObject<HTMLElement | null>;
}): number | undefined => {
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!isOpen) {
      setMaxHeight(undefined);
      return;
    }

    let rafId: number | null = null;

    const recompute = () => {
      const anchor = anchorRef.current;
      const wrapper = wrapperRef.current;
      if (!anchor || !wrapper) return;

      const available = Math.max(
        getAvailableHeight(anchor, actualSide),
        MIN_AVAILABLE_HEIGHT
      );

      setMaxHeight(wrapper.scrollHeight > available ? available : undefined);
    };

    const schedule = () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(recompute);
    };

    schedule();
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(schedule);
    if (wrapperRef.current) resizeObserver?.observe(wrapperRef.current);
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
    };
  }, [isOpen, actualSide, anchorRef, wrapperRef]);

  return maxHeight;
};

type StyledPopoverContainerProps = {
  isOpen: boolean;
  preferredSide: PopoverSide;
  /** The popover `id`, whose `anchor-name` the arrow points at via CSS. */
  anchorId: string;
  anchorRef: RefObject<HTMLElementWithAnchor | null>;
  onMouseEnter?: (e: React.MouseEvent) => void;
  onMouseLeave?: (e: React.MouseEvent) => void;
  disablePointerEvents?: boolean;
  /**
   * Keep re-measuring the arrow each frame while open, for anchors that move
   * after the popover opens (`PopoverAtPoint`). Static-element anchors leave
   * this off and measure once on open.
   */
  trackAnchor?: boolean;
  children: ReactNode;
};

/**
 * Shared styled container for the popover2025 styled popovers: a positioned
 * wrapper with an arrow pointing back at the anchor and a padded card body.
 * Detects flips from `position-try-fallbacks` and re-anchors the arrow.
 */
export const StyledPopoverContainer = ({
  isOpen,
  preferredSide,
  anchorId,
  anchorRef,
  onMouseEnter,
  onMouseLeave,
  disablePointerEvents,
  trackAnchor,
  children,
}: StyledPopoverContainerProps): ReactNode => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const actualSide = useRenderedSide({
    isOpen,
    preferredSide,
    anchorRef,
    popoverRef: containerRef,
    trackAnchor,
  });
  const maxHeight = usePopoverMaxHeight({
    isOpen,
    actualSide,
    anchorRef,
    wrapperRef,
  });

  return (
    <PopoverPositioningContainer
      ref={containerRef}
      $side={actualSide}
      $gap={POPOVER_GAP}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={disablePointerEvents ? { pointerEvents: 'none' } : undefined}
    >
      <PopoverArrow
        $side={preferredSide}
        $anchorName={anchorNameForId(anchorId)}
        $gap={POPOVER_GAP}
      />
      <PopoverWrapper ref={wrapperRef} $maxHeight={maxHeight}>
        {children}
      </PopoverWrapper>
    </PopoverPositioningContainer>
  );
};

/**
 * Detects the rendered side of the popover by comparing bounding rects, since
 * `position-try-fallbacks` may flip it to a different side than preferred.
 */
const computeActualSide = (
  preferredSide: PopoverSide,
  anchorRect: DOMRect,
  popoverRect: DOMRect
): PopoverSide => {
  if (preferredSide === 'top' || preferredSide === 'bottom') {
    const anchorCenterY = anchorRect.top + anchorRect.height / 2;
    const popoverCenterY = popoverRect.top + popoverRect.height / 2;
    return popoverCenterY < anchorCenterY ? 'top' : 'bottom';
  }
  const anchorCenterX = anchorRect.left + anchorRect.width / 2;
  const popoverCenterX = popoverRect.left + popoverRect.width / 2;
  return popoverCenterX < anchorCenterX ? 'left' : 'right';
};

/**
 * Tracks the rendered side, re-detecting on open, on popover resize (via
 * `ResizeObserver`), and — with `trackAnchor` — every frame for anchors that
 * move (`PopoverAtPoint`). Re-renders only when the side changes.
 */
export const useRenderedSide = ({
  isOpen,
  preferredSide,
  anchorRef,
  popoverRef,
  trackAnchor,
}: {
  isOpen: boolean;
  preferredSide: PopoverSide;
  anchorRef: RefObject<HTMLElementWithAnchor | null>;
  popoverRef: RefObject<HTMLElement | null>;
  trackAnchor?: boolean;
}): PopoverSide => {
  const [actualSide, setActualSide] = useState<PopoverSide>(preferredSide);

  useEffect(() => {
    if (!isOpen) {
      setActualSide(preferredSide);
      return;
    }

    const measure = () => {
      const anchor = anchorRef.current;
      const popover = popoverRef.current;
      if (!anchor || !popover) return;
      const next = computeActualSide(
        preferredSide,
        anchor.getBoundingClientRect(),
        popover.getBoundingClientRect()
      );
      setActualSide(prev => (prev === next ? prev : next));
    };

    let rafId = requestAnimationFrame(function frame() {
      measure();
      if (trackAnchor) rafId = requestAnimationFrame(frame);
    });

    const popover = popoverRef.current;
    // `ResizeObserver` is absent in some non-browser environments (e.g. jsdom
    // without a polyfill); fall back to the one-shot measure above there.
    const resizeObserver =
      !trackAnchor && popover && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(measure);
          })
        : null;
    if (resizeObserver && popover) resizeObserver.observe(popover);

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
    };
  }, [isOpen, preferredSide, anchorRef, popoverRef, trackAnchor]);

  return actualSide;
};
